import { uniq } from 'ramda';
import type { Displays_AudienceSource, Displays_AudienceVersion } from 'c-sdk';

/**
 * UL-7904 -- the campaign feature flags are tri-state.
 *
 * `null` on the campaign means "inherit from the buyer" and is the default; `true`/`false` are
 * overrides in either direction. The API resolves `campaign ?? buyer` into the effective value, so
 * an explicit `false` now turns a feature off even when the buyer has it on -- which is the point
 * of the change, and why null and false must never be collapsed together.
 *
 * Toggle buttons compare their value by identity and cannot bind `null`, so the control works in
 * these string sentinels and maps back at the Controller boundary.
 */
export type FeatureFlagChoice = 'inherit' | 'on' | 'off';

export const featureFlagToChoice = (value: boolean | null | undefined): FeatureFlagChoice => {
    if (value === null || value === undefined) return 'inherit';

    return value ? 'on' : 'off';
};

export const choiceToFeatureFlag = (choice: FeatureFlagChoice): boolean | null => {
    if (choice === 'inherit') return null;

    return choice === 'on';
};

/**
 * Whether the campaign is overriding its buyer, i.e. whether this is worth warning about.
 *
 * Inheriting is never an override, however the buyer is set. Agreeing with the buyer is not one
 * either -- that value is redundant rather than surprising, and warning about it would put a
 * warning in front of anyone who simply set it to match.
 */
export const overridesBuyerFeatureFlag = (
    campaignValue: boolean | null | undefined,
    buyerValue: boolean | null | undefined,
): boolean => {
    if (campaignValue === null || campaignValue === undefined) return false;

    return campaignValue !== Boolean(buyerValue);
};

export const uniqueCreativeValues = (value: string) =>
    uniq(
        value
            ?.split('\n')
            ?.map(id => id.trim())
            ?.filter(id => id.length > 0) ?? [],
    );

/**
 * UL-8034 -- the Audience Versions picker.
 *
 * Versions are grouped by source id, never by display name: two sources are "Route" (1 and 2) and
 * two are "Geopath (18+)" (3 and 8). The technical `source_name` (the API's `name`) is what tells
 * them apart, so it is always shown under the friendly one.
 *
 * Without `displays.audience_source.read` the picker has no source data at all (the API throws for
 * `include=source`), so every option's `source` is null and the list is flat.
 */
export type AudienceVersionOption = {
    id: number;
    /** Null for a stored id with no loaded version (a soft-deleted one). */
    version: Displays_AudienceVersion | null;
    source: Displays_AudienceSource | null;
};

/** The friendly name, falling back to the technical one when the API has none. */
export const audienceSourceLabel = (source: Displays_AudienceSource) =>
    source.display_name?.trim() ? source.display_name : source.name;

/** Active sources first, then by friendly name (ignoring case), then by technical name. */
export const compareAudienceSources = (a: Displays_AudienceSource, b: Displays_AudienceSource) => {
    if (a.active !== b.active) return a.active ? -1 : 1;

    return (
        audienceSourceLabel(a).localeCompare(audienceSourceLabel(b), undefined, {
            sensitivity: 'base',
        }) ||
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
        a.id - b.id
    );
};

/**
 * Newest first. Not by id alone: Route ids 2 to 12 are out of release order, and Geopath versions
 * mostly share one release id and revision, so the id only breaks ties.
 */
export const compareAudienceVersions = (a: Displays_AudienceVersion, b: Displays_AudienceVersion) =>
    (b.release_id ?? 0) - (a.release_id ?? 0) ||
    (b.release_revision ?? 0) - (a.release_revision ?? 0) ||
    b.id - a.id;

/**
 * Every loaded version once, in group order then version order (MUI `groupBy` needs the options
 * pre-sorted by group). A version whose source is not in `sources` uses its own `source`, if any.
 */
export const buildAudienceVersionOptions = (
    versions: Displays_AudienceVersion[],
    sources: Displays_AudienceSource[] | null,
): AudienceVersionOption[] => {
    const sourcesById = new Map((sources ?? []).map(source => [source.id, source]));
    const seen = new Set<number>();
    const entries = versions.reduce((acc, version) => {
        if (seen.has(version.id)) return acc;
        seen.add(version.id);
        const source =
            sources == null
                ? null
                : sourcesById.get(version.audience_source_id) ?? version.source ?? null;
        acc.push({ version, source });
        return acc;
    }, [] as { version: Displays_AudienceVersion; source: Displays_AudienceSource | null }[]);

    return entries
        .sort((a, b) => {
            if (a.source && b.source && a.source.id !== b.source.id) {
                return compareAudienceSources(a.source, b.source);
            }
            // A version with no known source sorts after every grouped one.
            if (a.source && !b.source) return -1;
            if (!a.source && b.source) return 1;
            return compareAudienceVersions(a.version, b.version);
        })
        .map(({ version, source }) => ({ id: version.id, version, source }));
};

/**
 * The options the picker's value is built from: each selected id, in stored order, mapped to its
 * loaded option, or to a placeholder so that MUI does not drop an id it cannot find.
 */
export const selectedAudienceVersionOptions = (
    ids: number[],
    options: AudienceVersionOption[],
): AudienceVersionOption[] => {
    const byId = new Map(options.map(option => [option.id, option]));
    return ids.map(id => byId.get(id) ?? { id, version: null, source: null });
};

/** Case-insensitive "contains" on the version name and, when known, both source names. */
export const audienceVersionMatchesSearch = (option: AudienceVersionOption, search: string) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;

    return [option.version?.version_name, option.source?.display_name, option.source?.name].some(
        text => text?.toLowerCase().includes(term),
    );
};

export type AudienceVersionFilters = {
    selectedIds: number[];
    showInactive: boolean;
    /** Nothing chosen means every source. */
    sourceIds: number[];
    search: string;
};

/**
 * What the open list shows. Selected versions always stay, whatever the filters; a placeholder for
 * an unavailable id never does (it has nothing to pick, and lives in the selected list). Whether a
 * source is inactive can only be known with source data, so without it nothing is hidden for that.
 */
export const visibleAudienceVersionOptions = (
    options: AudienceVersionOption[],
    { selectedIds, showInactive, sourceIds, search }: AudienceVersionFilters,
) =>
    options.filter(option => {
        if (!option.version) return false;
        if (selectedIds.includes(option.id)) return true;
        if (!showInactive && option.source && !option.source.active) return false;
        if (sourceIds.length > 0 && !sourceIds.includes(option.version.audience_source_id)) {
            return false;
        }
        return audienceVersionMatchesSearch(option, search);
    });

/** The source filter's choices, in group order; inactive ones only while they are shown. */
export const audienceSourceFilterOptions = (
    sources: Displays_AudienceSource[],
    showInactive: boolean,
) => sources.filter(source => showInactive || source.active).sort(compareAudienceSources);

/** Turning "Show inactive sources" off drops any inactive source from the filter selection. */
export const withoutInactiveSourceIds = (
    sourceIds: number[],
    sources: Displays_AudienceSource[],
) => {
    const activeIds = new Set(sources.filter(source => source.active).map(source => source.id));
    return sourceIds.filter(id => activeIds.has(id));
};

/** Position 0 is the primary audience; every later one is a fallback, in order. */
export const audienceVersionRole = (index: number): 'primary' | 'fallback' =>
    index === 0 ? 'primary' : 'fallback';

/**
 * The release days a version row shows, as `yyyy-MM-dd`. The API sends `date_first_release` as a
 * datetime but `date_last_revision` as a bare date (the model casts a column that does not exist,
 * `date_last_release`), so both are cut to the day here. Missing or blank is null: show nothing.
 */
export const audienceVersionReleaseDays = (
    version: Pick<Displays_AudienceVersion, 'date_first_release' | 'date_last_revision'> | null,
) => {
    const day = (value: string | null | undefined) => value?.split('T')[0]?.trim() || null;
    return {
        firstRelease: day(version?.date_first_release),
        lastRevision: day(version?.date_last_revision),
    };
};
